import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Lock, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const TIMES = ["09:00", "11:00", "14:00", "16:00", "18:00", "20:00"];
const LEVELS = ["Beginner", "Intermediate", "Advanced", "AP/College"];

export default function SessionRequestModal({ tutor, open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(tutor?.subjects?.[0] || "English");
  const [level, setLevel] = useState("Intermediate");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [firstSession, setFirstSession] = useState(null); // {first_session, discount_percent}

  // Debounced check: when a plausible email is typed, see if they qualify for the first-session discount
  useEffect(() => {
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFirstSession(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/payments/first-session-check", { params: { email: trimmed } })
        .then((r) => setFirstSession(r.data))
        .catch(() => setFirstSession(null));
    }, 350);
    return () => clearTimeout(t);
  }, [email]);

  const fullRate = Number(tutor?.hourly_rate || 0);
  const discountPct = firstSession?.first_session ? 50 : 0;
  const payable = discountPct ? fullRate / 2 : fullRate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please pick a date"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/book-session", {
        tutor_id: tutor.id,
        student_name: name,
        student_email: email,
        subject,
        level,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time || null,
        message,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start booking");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="session-request-modal">
        <DialogHeader>
          <DialogTitle>Book a session with {tutor?.name}</DialogTitle>
          <DialogDescription>
            One hour · <span className="font-semibold text-foreground">${fullRate}</span>.
            You'll be redirected to Stripe for secure checkout. Declined requests are auto-refunded.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="s-name">Your name</Label>
              <Input
                id="s-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="session-request-student-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-email">Email</Label>
              <Input
                id="s-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="session-request-student-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger data-testid="session-request-subject-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(tutor?.subjects || ["French", "English", "Spanish", "Math"]).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Level</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="session-request-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Preferred date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="session-request-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().toDateString())}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Preferred time</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="session-request-time-slot">
                  <SelectValue placeholder="Pick a slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIMES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="s-msg">Learning goals (optional)</Label>
            <Textarea
              id="s-msg"
              rows={3}
              placeholder="What would you like to work on?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="session-request-message"
            />
          </div>

          {discountPct > 0 && (
            <div
              className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 flex items-center gap-3"
              data-testid="first-session-discount-banner"
            >
              <div className="h-9 w-9 rounded-full bg-emerald-100 dark:bg-emerald-900/60 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                  First-session 50% off
                  <span
                    className="inline-flex items-center rounded-md border border-emerald-300 dark:border-emerald-800 bg-white dark:bg-emerald-950/70 px-1.5 py-0.5 font-mono text-[11px] tracking-widest text-emerald-700 dark:text-emerald-200"
                    data-testid="first-session-promo-code"
                  >
                    FIRST50
                  </span>
                </p>
                <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
                  Code auto-applied at checkout — you'll see <code className="font-mono">FIRST50</code> on the Stripe receipt.
                </p>
              </div>
              <Badge className="rounded-full bg-emerald-600 hover:bg-emerald-600 text-white font-mono">
                −50%
              </Badge>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Total</p>
              {discountPct > 0 ? (
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-bold" data-testid="session-total-amount">
                    ${payable.toFixed(2)}
                  </span>
                  <span className="text-sm line-through text-muted-foreground" data-testid="session-total-original">
                    ${fullRate.toFixed(2)}
                  </span>
                  <span className="text-xs text-muted-foreground">USD</span>
                </div>
              ) : (
                <p className="font-heading text-2xl font-bold" data-testid="session-total-amount">
                  ${fullRate.toFixed(2)} USD
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full sm:w-auto rounded-full h-11 px-6"
              disabled={submitting}
              data-testid="session-request-submit-button"
            >
              <Lock className="h-4 w-4 mr-1.5" />
              {submitting ? "Redirecting…" : "Pay & book"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
